import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useProfile } from './useProfile';
import { useTrialStatus } from './useTrialStatus';

// 儲存限制常數（單位：bytes）- 預設值
const MB = 1024 * 1024;

interface StorageLimits {
  monthly: number;
  total: number;
}

interface DatabaseStorageLimits {
  cloudStorageMonthlyBytes?: number;
  cloudStorageTotalBytes?: number;
}

// 預設限制（資料庫無法訪問時的最終回退值）
const FALLBACK_LIMITS: StorageLimits = {
  monthly: 500 * MB,
  total: 500 * MB,
};

interface CanUploadResult {
  allowed: boolean;
  reason?: 'monthly_limit' | 'total_limit' | 'trial_expired';
}

interface CloudStorageLimitReturn {
  // 限制值
  monthlyLimit: number;
  totalLimit: number;
  // 使用量
  usedThisMonth: number;
  usedTotal: number;
  // 剩餘量
  remainingThisMonth: number;
  remainingTotal: number;
  // 百分比
  monthlyUsagePercent: number;
  totalUsagePercent: number;
  // 試用狀態
  isTrialExpired: boolean;
  // 方法
  canUpload: (fileSize: number) => CanUploadResult;
  formatBytes: (bytes: number) => string;
  // 載入狀態
  loading: boolean;
}

/**
 * 雲端儲存限制 Hook
 * 
 * 用於檢查用戶的雲端儲存使用量和限制
 * 從資料庫中的 subscription_plans 表讀取限制配置
 * 
 * 注意：免費用戶試用到期後，禁止所有雲端上傳
 * 
 * @example
 * const { canUpload, usedTotal, totalLimit } = useCloudStorageLimit();
 * 
 * if (!canUpload(fileSize).allowed) {
 *   toast.error('儲存空間不足');
 * }
 */
export function useCloudStorageLimit(): CloudStorageLimitReturn {
  const { profile, loading: profileLoading } = useProfile();
  const { isTrialExpired } = useTrialStatus();
  
  const tier = profile?.subscription_tier || 'free';
  const isPaidUser = tier !== 'free';

  // 從資料庫獲取儲存限制（包括 Free）
  const { data: storageLimits, isLoading: limitsLoading } = useQuery({
    queryKey: ['cloud-storage-limits', tier],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('limits')
        .eq('name', tier)
        .eq('is_active', true)
        .maybeSingle();

      if (error || !data?.limits) {
        return FALLBACK_LIMITS;
      }

      const dbLimits = data.limits as DatabaseStorageLimits;
      
      return {
        monthly: dbLimits.cloudStorageMonthlyBytes ?? FALLBACK_LIMITS.monthly,
        total: dbLimits.cloudStorageTotalBytes ?? FALLBACK_LIMITS.total,
      };
    },
    staleTime: 1000 * 60 * 10, // 10 分鐘快取
    enabled: !!tier,
  });

  const limits = storageLimits || FALLBACK_LIMITS;
  const loading = profileLoading || limitsLoading;
  
  const usedThisMonth = profile?.cloud_storage_uploaded_this_month || 0;
  const usedTotal = profile?.cloud_storage_total_bytes || 0;
  
  const remainingThisMonth = Math.max(0, limits.monthly - usedThisMonth);
  const remainingTotal = Math.max(0, limits.total - usedTotal);
  
  const monthlyUsagePercent = limits.monthly > 0 
    ? Math.min(100, Math.round((usedThisMonth / limits.monthly) * 100))
    : 0;
    
  const totalUsagePercent = limits.total > 0
    ? Math.min(100, Math.round((usedTotal / limits.total) * 100))
    : 0;

  const canUpload = useMemo(() => {
    return (fileSize: number): CanUploadResult => {
      // 免費用戶試用到期後，禁止所有上傳
      if (!isPaidUser && isTrialExpired) {
        return { allowed: false, reason: 'trial_expired' };
      }
      // 檢查月度限制
      if (usedThisMonth + fileSize > limits.monthly) {
        return { allowed: false, reason: 'monthly_limit' };
      }
      // 檢查總量限制
      if (usedTotal + fileSize > limits.total) {
        return { allowed: false, reason: 'total_limit' };
      }
      return { allowed: true };
    };
  }, [usedThisMonth, usedTotal, limits, isPaidUser, isTrialExpired]);

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  };

  return {
    monthlyLimit: limits.monthly,
    totalLimit: limits.total,
    usedThisMonth,
    usedTotal,
    remainingThisMonth,
    remainingTotal,
    monthlyUsagePercent,
    totalUsagePercent,
    isTrialExpired: !isPaidUser && isTrialExpired,
    canUpload,
    formatBytes,
    loading,
  };
}
